import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { DashboardRecord } from "@/lib/dashboard/types";
import { useRestClient } from "@/lib/restClient";
import { exportComposition } from "@/pages/admin/configuration/configurationApi";
import { exportFileName } from "@/pages/admin/configuration/configurationModel";
import { downloadTransfer } from "@/pages/admin/configuration/dataTransferApi";
import { apiErrorMessage } from "@/pages/admin/useTenantAdmin";
import { clearDashboardDraftForRecord } from "./dashboardDraft";

/** A dashboard the action could not complete, with the reason. */
export interface BulkFailure {
  dashboard: DashboardRecord;
  reason: string;
}

export interface BulkOutcome {
  done: DashboardRecord[];
  failed: BulkFailure[];
}

/** Can the bulk delete remove it: not protected, and the caller may change it. */
export function canBulkDelete(dashboard: DashboardRecord): boolean {
  return !dashboard.protected && dashboard.access?.editable !== false;
}

/** Can it be exported: only a published version is moved between platforms. */
export function canBulkExport(dashboard: DashboardRecord): boolean {
  return dashboard.state !== "draft";
}

/**
 * Browsers group downloads started in the same instant (and may ask once to
 * allow several): a short pause between files keeps each one its own download.
 */
const DOWNLOAD_SPACING_MS = 400;
const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Distinct file names, so two dashboards with the same slug never overwrite each other. */
export function exportFileNames(dashboards: readonly DashboardRecord[]): string[] {
  const used = new Map<string, number>();
  return dashboards.map((dashboard) => {
    const base = exportFileName(dashboard.slug);
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    return seen === 0 ? base : base.replace(/\.json$/, `-${seen + 1}.json`);
  });
}

/**
 * Delete or export several dashboards in one go, one after the other so each
 * failure is reported against its dashboard and the others still go through.
 * Exports download one file per dashboard, as a single export does, and each is
 * kept in the Data Transfer history.
 */
export function useBulkDashboardActions() {
  const api = useRestClient();
  const client = useQueryClient();

  const remove = useMutation({
    mutationFn: async (dashboards: readonly DashboardRecord[]): Promise<BulkOutcome> => {
      const outcome: BulkOutcome = { done: [], failed: [] };
      for (const dashboard of dashboards) {
        try {
          await api(`/api/dashboards/${dashboard.id}`, { method: "DELETE" });
          clearDashboardDraftForRecord(dashboard.id);
          outcome.done.push(dashboard);
        } catch (cause) {
          outcome.failed.push({ dashboard, reason: apiErrorMessage(cause) });
        }
      }
      return outcome;
    },
    onSettled: () => void client.invalidateQueries({ queryKey: ["dashboards"] }),
  });

  const exportAll = useMutation({
    mutationFn: async (dashboards: readonly DashboardRecord[]): Promise<BulkOutcome> => {
      const outcome: BulkOutcome = { done: [], failed: [] };
      const names = exportFileNames(dashboards);
      for (const [index, dashboard] of dashboards.entries()) {
        try {
          const result = await exportComposition(api, "dashboards", dashboard.id);
          if (outcome.done.length > 0) await pause(DOWNLOAD_SPACING_MS);
          downloadTransfer(result.configuration, names[index]);
          outcome.done.push(dashboard);
        } catch (cause) {
          outcome.failed.push({ dashboard, reason: apiErrorMessage(cause) });
        }
      }
      return outcome;
    },
    onSettled: () => void client.invalidateQueries({ queryKey: ["data-transfers"] }),
  });

  return { remove, exportAll };
}
